import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      question: 'What is your typical project timeline?',
      answer: `Project timelines vary based on scope and complexity. An MVP typically takes 8-12 weeks, while a full-featured SaaS platform requires 16-24 weeks. Enterprise solutions may extend beyond 24 weeks. We provide detailed project roadmaps during discovery phase with clear milestones and deliverables. Our agile methodology allows for iterative development with regular demos and feedback cycles every 2 weeks.`
    },
    {
      question: 'How do you ensure code quality and security?',
      answer: `We follow industry best practices including code reviews, automated testing (unit, integration, and end-to-end), continuous integration/deployment pipelines, and security audits. Our development process includes static code analysis, dependency vulnerability scanning, and penetration testing. We adhere to OWASP security guidelines and implement security measures at every layer of the application. All code is version-controlled with Git, and we maintain comprehensive documentation.`
    },
    {
      question: 'What happens after the project launch?',
      answer: `Post-launch, we provide comprehensive support including bug fixes, performance monitoring, and technical assistance. Our support packages range from 3 to 12 months depending on your plan. We offer ongoing maintenance contracts for continuous updates, security patches, and feature enhancements. You'll receive detailed documentation, training sessions for your team, and access to our support portal. We also provide analytics setup to track user behavior and application performance.`
    },
    {
      question: 'Can you work with our existing development team?',
      answer: `Absolutely! We excel at collaborative partnerships and can integrate seamlessly with your existing team. We can provide dedicated developers to augment your team, work as a complete outsourced development partner, or offer technical consulting and architecture guidance. Our team is experienced in various collaboration models including staff augmentation, dedicated teams, and hybrid approaches. We use standard tools like Slack, Jira, and GitHub for seamless communication and project management.`
    },
    {
      question: 'Do you provide UI/UX design services?',
      answer: `Yes, we offer comprehensive UI/UX design services as part of our development packages or as standalone services. Our design process includes user research, wireframing, interactive prototyping, visual design, and usability testing. We create design systems that ensure consistency across your application and provide design files in Figma or Adobe XD. Our designers work closely with developers to ensure pixel-perfect implementation and optimal user experience.`
    },
    {
      question: 'What technologies do you specialize in?',
      answer: `We specialize in modern web technologies including React, Next.js, Node.js, Python, and various cloud platforms (AWS, Google Cloud, Azure). For mobile development, we use React Native and Flutter for cross-platform solutions, as well as native iOS (Swift) and Android (Kotlin) development. Our backend expertise includes microservices architecture, RESTful and GraphQL APIs, and various databases (PostgreSQL, MongoDB, Redis). We stay current with emerging technologies and can recommend the best stack for your specific requirements.`
    },
    {
      question: 'How do you handle project changes and scope adjustments?',
      answer: `We use agile methodology which accommodates changes throughout the development process. During each sprint planning session, we can reprioritize features and adjust scope based on your evolving needs. We maintain transparent communication about how changes impact timeline and budget. Our change request process includes impact analysis, revised estimates, and formal approval before implementation. We believe in flexibility while maintaining project integrity and delivering value.`
    },
    {
      question: 'What is your pricing model?',
      answer: `We offer flexible pricing models including fixed-price projects for well-defined scopes, time and materials for evolving requirements, and dedicated team arrangements for long-term partnerships. Our pricing is transparent with no hidden costs. We provide detailed proposals breaking down costs by phase, feature, and resource allocation. Payment terms typically include milestone-based payments aligned with deliverables. We're happy to discuss custom arrangements that align with your budget and business model.`
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4 md:mb-6">
            <Icon name="HelpCircle" size={18} color="var(--color-primary)" />
            <span className="text-xs md:text-sm font-medium font-cta text-primary">
              Common Questions
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-headline text-foreground mb-4 md:mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-base md:text-lg text-muted-foreground font-body">
            Find answers to common questions about our services, process, and partnerships
          </p>
        </div>

        <div className="space-y-4">
          {faqs?.map((faq, index) => (
            <div
              key={index}
              className="bg-card border border-border rounded-lg overflow-hidden transition-all duration-200 hover:border-primary/50"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-4 md:p-6 text-left hover:bg-muted/50 transition-colors duration-200"
              >
                <span className="text-base md:text-lg font-semibold font-headline text-foreground pr-4">
                  {faq?.question}
                </span>
                <Icon
                  name={openIndex === index ? 'ChevronUp' : 'ChevronDown'}
                  size={24}
                  className="flex-shrink-0 text-primary"
                />
              </button>
              {openIndex === index && (
                <div className="px-4 md:px-6 pb-4 md:pb-6">
                  <p className="text-sm md:text-base text-muted-foreground font-body leading-relaxed">
                    {faq?.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 text-center bg-muted/50 rounded-lg p-6 md:p-8">
          <Icon name="MessageSquare" size={48} color="var(--color-primary)" className="mx-auto mb-4" />
          <h3 className="text-xl md:text-2xl font-bold font-headline text-foreground mb-3">
            Still Have Questions?
          </h3>
          <p className="text-sm md:text-base text-muted-foreground font-body mb-6">
            Our team is here to help. Schedule a free consultation to discuss your specific needs.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center space-x-2 bg-primary text-primary-foreground px-6 md:px-8 py-3 md:py-4 rounded-md font-medium font-cta text-sm md:text-base hover:bg-primary/90 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <Icon name="Calendar" size={18} />
            <span>Schedule Consultation</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;